import React, { useState, useEffect } from 'react'
import { ALL_GALLERY_ITEMS } from '../data/siteData'
import { getOptimizedImageUrl } from '../lib/imageHelper'
import Lightbox from './Lightbox'

export default function GallerySection({
  galleryKey,
  title = 'Billeder fra udførte opgaver',
  subtitle = 'Se eksempler på vores arbejde og det færdige resultat i haven',
  limit = 6,
  showLinkToFull = false
}) {
  const [items, setItems] = useState([])
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    async function loadGallery() {
      try {
        // Fetch from CMS endpoint
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/galleri')
        if (res.ok) {
          const json = await res.json()
          const cmsGalleries = json.items || []

          if (galleryKey) {
            // Find specific gallery
            const matched = cmsGalleries.find(
              (g) => g.gallery_key === galleryKey || g.service_slug === galleryKey
            )
            if (matched && matched.images && matched.images.length > 0) {
              const formatted = matched.images.map((imgUrl, idx) => ({
                id: `${matched.gallery_key}-${idx}`,
                title: matched.title,
                image: imgUrl,
                caption: matched.description,
                category: matched.category
              }))
              if (isMounted) {
                setItems(formatted)
                setLoading(false)
                return
              }
            }
          } else {
            // Flatten all CMS galleries
            const allImages = []
            cmsGalleries.forEach((g) => {
              if (Array.isArray(g.images)) {
                g.images.forEach((imgUrl, idx) => {
                  allImages.push({
                    id: `${g.gallery_key || 'g'}-${idx}`,
                    title: g.title,
                    image: imgUrl,
                    caption: g.description,
                    category: g.category,
                    galleryKey: g.gallery_key
                  })
                })
              }
            })
            if (allImages.length > 0 && isMounted) {
              setItems(allImages)
              setLoading(false)
              return
            }
          }
        }
      } catch (e) {
        // Fallback gracefully to seeded dataset
      }

      // Fallback
      if (isMounted) {
        let filtered = ALL_GALLERY_ITEMS
        if (galleryKey) {
          filtered = ALL_GALLERY_ITEMS.filter((item) => item.galleryKey === galleryKey)
        }
        setItems(filtered)
        setLoading(false)
      }
    }

    loadGallery()

    return () => {
      isMounted = false
    }
  }, [galleryKey])

  const displayedItems = limit ? items.slice(0, limit) : items

  if (!loading && displayedItems.length === 0) {
    return null
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-[#015205]">
            Projekteksempler
          </span>
          <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-1">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 text-sm text-stone-600 max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>

        {showLinkToFull && (
          <a
            href="/galleri"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#015205] hover:text-[#026b07] transition-colors whitespace-nowrap"
          >
            <span>Se alle billeder i galleriet</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
          </a>
        )}
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayedItems.map((item, idx) => (
          <div
            key={item.id || idx}
            onClick={() => setActiveLightboxIndex(idx)}
            className="group relative overflow-hidden rounded-xl bg-stone-900 border border-stone-200/80 shadow-sm cursor-pointer"
            role="button"
            tabIndex={0}
            aria-label={`Åbn ${item.title || 'billede'} i fuld skærm`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setActiveLightboxIndex(idx)
              }
            }}
          >
            <div className="aspect-4/3 w-full overflow-hidden bg-stone-800">
              <img
                src={getOptimizedImageUrl(item.image, { width: 700, height: 525 })}
                alt={item.title ? `${item.title} - JT Knudsen Hjortshøj` : 'Udført anlægsarbejde af JT Knudsen'}
                onError={(e) => {
                  if (e.currentTarget.src !== item.image) {
                    e.currentTarget.src = item.image
                  }
                }}
                className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                loading="lazy"
              />
            </div>

            {/* Overlay hint */}
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider">
                    {item.category || 'JT Knudsen'}
                  </span>
                  <p className="text-sm font-bold text-white leading-snug">
                    {item.title}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#015205] text-white flex items-center justify-center shrink-0 ml-2 shadow">
                  <i className="fa-solid fa-expand text-xs" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {activeLightboxIndex !== null && (
        <Lightbox
          images={displayedItems}
          activeIndex={activeLightboxIndex}
          onClose={() => setActiveLightboxIndex(null)}
          onSelectIndex={(newIdx) => setActiveLightboxIndex(newIdx)}
        />
      )}
    </div>
  )
}
